import * as React from 'react'
import { isHotkey } from 'is-hotkey'
import { clsx } from 'clsx'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Button } from '~/sotano-kit/primitives/button'

import { getId } from '@basehub/utils/id/index'

import {
  blockTypesRecord,
  isInstanceBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { BlockIcon } from '~/components/block-icon'
import { getFromBlockMetadata } from '~/lib/tiptap/extensions/comment/utils'
import { AIIcon } from '~/components/icons/ai'
import { useTabsStore } from '../../tabs/store'
import { AIModelingInput } from './ai-modeling'

import { type Command } from '../slash-command'
import type {
  DocumentBlock,
  ComponentBlock,
  InstanceBlock,
  BlockType,
  Block,
  ReferenceBlock,
} from '@basehub/data/replicache/index'
import { ENABLE_SCHEMA_ASSISTANT } from '~/app/api/ai/schema-assistant/_constants'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useEditorStore } from '../../editor-store'
import { useRepoStore } from '../../repo-store'
import { CreateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'

export const BlockEmptyState: React.FC<{
  block: DocumentBlock | ComponentBlock | InstanceBlock | ReferenceBlock
  path: string
  parent: Block | null
}> = ({ block, path, parent }) => {
  const templateRefs = React.useRef<HTMLButtonElement[]>([])
  const templateWrapperRef = React.useRef<HTMLDivElement>(null)
  const [focusedTemplateIndex, setFocusedTemplateIndex] = React.useState(0)
  const [isPointerIdle, setIsPointerIdle] = React.useState(true)
  const aiGeneration = useEditorStore((s) => s.aiGeneration)
  const setAiGeneration = useEditorStore((s) => s.setAiGeneration)
  const setFocus = useRepoStore((s) => s.setFocus)
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const removeTab = useTabsStore((s) => s.removeTab)
  const addTab = useTabsStore((s) => s.addTab)

  const selectCommandsToAdd: Command[] = React.useMemo(() => {
    return [
      getFromBlockMetadata('text'),
      getFromBlockMetadata('rich-text'),
      getFromBlockMetadata('date'),
      getFromBlockMetadata('media'),
      getFromBlockMetadata('code-snippet'),
    ]
  }, [])

  const templateList: {
    name: string | null
    disabled?: boolean
    templates: {
      label: React.ReactNode
      description: React.ReactNode
      icon: (isHighlighted: boolean) => React.ReactNode
      disabled?: boolean
      onSelect?: () => Promise<void> | void
    }[]
  }[] = React.useMemo(() => {
    const turnIntoCommands = []

    // IF AI is enabled, add the AI command
    if (ENABLE_SCHEMA_ASSISTANT) {
      turnIntoCommands.push({
        label: <span className="text-dark-control-base">Ask AI to model</span>,
        description: `— ask stuff like "model ${block.title || 'a blog'}"`,
        icon: () => <AIIcon width={16} height={16} />,
        onSelect: () => {
          setAiGeneration({
            status: 'idle',
            generatedBlockIds: [],
          })
        },
      })
    }

    // Every block can start with a blank block
    turnIntoCommands.push({
      label: 'Start blank',
      description: `— start with a blank ${block.type}`,
      icon: (highlighted = false) => (
        <BlockIcon
          block={block}
          iconProps={{
            width: 16,
            height: 16,
            className: clsx(
              highlighted ? 'text-dark-dim' : 'text-dark-muted',
              'pointer-events-none'
            ),
          }}
        />
      ),
      onSelect: async () => {
        if (!replicache) return
        const id = getId()

        const params: CreateBlockParams = {
          data: { id, type: 'comment' },
          position: { type: 'last' },
          parentInstanceChildTarget: block.instanceChildTarget,
          path,
          isoNow: new Date().toISOString(),
          idSeed: getId(),
        }

        await replicache.mutate.createBlock(params)

        setFocus({ type: 'block:value', blockId: id })
      },
    })

    // if parentBlock is a (list, union or component) and block is a component, we cant change it to a document
    const parentIsLayoutBlock =
      parent?.type === 'list' ||
      parent?.type === 'component' ||
      parent?.type === 'reference'
    if (
      (!parentIsLayoutBlock && block.type === 'component') ||
      block.type !== 'component'
    ) {
      turnIntoCommands.push({
        label: `Turn into ${
          block.type === 'document' ? 'component' : 'document'
        }`,
        description: `— for singleton types, such as a landing page, a button component`,
        icon: (highlighted = false) => (
          <BlockIcon
            block={{
              type: block.type === 'document' ? 'component' : 'document',
            }}
            iconProps={{
              width: 16,
              height: 16,
              className: clsx(
                highlighted ? 'text-dark-dim' : 'text-dark-muted',
                'pointer-events-none'
              ),
            }}
          />
        ),
        onSelect: async () => {
          if (!replicache) return

          await replicache.mutate.toggleMakeComponent({
            value: block.type === 'document',
            blockId: block.id,
            path,
            idSeed: getId(),
            isoNow: new Date().toISOString(),
          })

          const id = getId()

          await replicache.mutate.createBlock({
            data: { id, type: 'comment' },
            position: { type: 'last' },
            parentInstanceChildTarget: block.instanceChildTarget,
            path,
            isoNow: new Date().toISOString(),
            idSeed: getId(),
          })

          setFocus({ type: 'block:value', blockId: id })
        },
      })
    }

    // can't turn into collection if parent is a list
    if (
      block.type !== 'component' ||
      (block.type === 'component' &&
        parent?.type !== 'list' &&
        parent?.type !== 'reference')
    ) {
      turnIntoCommands.push({
        label: 'Turn into collection',
        description: `— for repeatable types such as blog posts, categories, an image gallery`,
        icon: (highlighted = false) => (
          <BlockIcon
            block={{
              type: 'list',
            }}
            iconProps={{
              width: 16,
              height: 16,
              className: clsx(
                highlighted ? 'text-dark-dim' : 'text-dark-muted',
                'pointer-events-none'
              ),
            }}
          />
        ),
        onSelect: async () => {
          if (!replicache) return
          const normalizedPath = normalizeStringPath({
            path,
            edgeId: block.id,
            includeEdge: false,
            format: 'string',
          })
          const { newBlock } = await replicache.mutate.changeBlockType({
            blockId: block.id,
            idSeed: getId(),
            isoNow: new Date().toISOString(),
            newBlockType: blockTypesRecord.list,
            columnIds: [],
            layout: 'table',
            typeOptions: null,
            path: normalizedPath,
            title: block.title,
          })
          removeTab([block.id]) // deleted
          addTab({ key: newBlock.id, partiallyOpen: true }) // new block
          setFocus({
            type: 'block:document-title',
            blockId: newBlock.id,
          })
        },
      })
    }

    const commands = [
      {
        name: null,
        templates: turnIntoCommands,
      },
      {
        name: 'Or start with a block',
        templates: [
          ...selectCommandsToAdd.map((command) => {
            const lowercaseDescription =
              command.description.charAt(0).toLowerCase() +
              command.description.slice(1)

            return {
              label: command.title,
              description: `— ${lowercaseDescription}`,
              icon: (highlighted = false) => (
                <BlockIcon
                  block={{ type: command.id as BlockType }}
                  iconProps={{
                    width: 16,
                    height: 16,
                    className: clsx(
                      highlighted ? 'text-dark-dim' : 'text-dark-muted',
                      'pointer-events-none'
                    ),
                  }}
                />
              ),
              onSelect: async () => {
                if (!replicache) return
                const id = getId()

                await replicache.mutate.createBlock({
                  data: { id, type: command.id as BlockType },
                  parentInstanceChildTarget: block.instanceChildTarget,
                  path,
                  position: { type: 'last' },
                  isoNow: new Date().toISOString(),
                  idSeed: getId(),
                })

                setFocus({ type: 'block:name', blockId: id })
              },
            }
          }),
        ],
      },
    ]

    return commands
  }, [
    addTab,
    block,
    setFocus,
    path,
    removeTab,
    replicache,
    setAiGeneration,
    selectCommandsToAdd,
    parent?.type,
  ])
  const flatTemplateList = React.useMemo(
    () => templateList.flatMap((group) => group.templates),
    [templateList]
  )

  React.useEffect(() => {
    if (!templateWrapperRef.current) return

    const tabContent = document.querySelector('div#tab-content')

    if (!tabContent) return

    const canUseArrowsToCycle = () => {
      return (
        document.activeElement instanceof HTMLTextAreaElement &&
        document.activeElement.dataset.documentTitle ===
          idToSourceBlockId(block)
      )
    }

    const cycleUp = ({ loop }: { loop: boolean }) => {
      if (!canUseArrowsToCycle()) return
      setFocusedTemplateIndex((prevState) => {
        const prevIndex = flatTemplateList.findLastIndex((template, index) => {
          if (template.disabled) return false

          const firstEnabledIndexInTemplateList = flatTemplateList.findIndex(
            (template) => !template.disabled
          )
          const isFirstItemInTemplateList =
            prevState === firstEnabledIndexInTemplateList

          if (!isFirstItemInTemplateList) return index < prevState
          if (!loop && isFirstItemInTemplateList) {
            return index === firstEnabledIndexInTemplateList
          }
          return true
        })

        return prevIndex
      })
    }

    const cycleDown = ({ loop }: { loop: boolean }) => {
      if (!canUseArrowsToCycle()) return
      setFocusedTemplateIndex((prevState) => {
        const nextIndex = flatTemplateList.findIndex((template, index) => {
          if (template.disabled) return false

          const lastEnabledIndexInTemplateList = flatTemplateList.findLastIndex(
            (template) => !template.disabled
          )
          const isLastEnabledItemInTemplateList =
            prevState === lastEnabledIndexInTemplateList

          if (!isLastEnabledItemInTemplateList) return index > prevState
          if (!loop && isLastEnabledItemInTemplateList) {
            return index === lastEnabledIndexInTemplateList
          }
          return true
        })

        return nextIndex
      })
    }

    const updateFocusedTemplateIndex = (e: KeyboardEvent) => {
      if (!document.activeElement) return

      const activeElementLivesInBlock = tabContent.contains(
        document.activeElement
      )
      const activeElementIsAnInput = [
        'INPUT',
        'A',
        'BUTTON',
        'TEXTAREA',
      ].includes(document.activeElement.tagName)

      if (!activeElementLivesInBlock && activeElementIsAnInput) return

      if (isHotkey('Enter', e)) {
        if (!canUseArrowsToCycle()) return
        const focusedTemplate = flatTemplateList[focusedTemplateIndex]

        if (!focusedTemplate?.disabled && document.activeElement) {
          focusedTemplate?.onSelect?.()
        }
      } else if (isHotkey('ArrowUp', e)) {
        e.preventDefault()
        cycleUp({ loop: true })
      } else if (isHotkey('ArrowDown', e)) {
        e.preventDefault()
        cycleDown({ loop: true })
      }
    }

    const cycleViaTabs = (e: KeyboardEvent) => {
      if (isHotkey('tab', e)) {
        const currentFocusedElementIsLastTemplate =
          templateRefs.current.findLast((template) => !template.disabled) ===
          e.target
        if (currentFocusedElementIsLastTemplate) return
        cycleDown({ loop: false })
      } else if (isHotkey('shift+tab', e)) {
        const currentFocusedElementIsFirstTemplate =
          templateRefs.current.find((template) => !template.disabled) ===
          e.target
        if (currentFocusedElementIsFirstTemplate) return
        cycleUp({ loop: false })
      }
    }

    const keepFocusedTemplateIndexInSync = (e: FocusEvent) => {
      if (!e.target) return
      setFocusedTemplateIndex(
        templateRefs.current.indexOf(e.target as HTMLButtonElement)
      )
    }

    document.addEventListener('keydown', updateFocusedTemplateIndex)
    templateWrapperRef.current.addEventListener('keydown', cycleViaTabs)
    templateWrapperRef.current.addEventListener(
      'focusin',
      keepFocusedTemplateIndexInSync,
      { once: true }
    )

    const wrapper = templateWrapperRef.current

    return () => {
      document.removeEventListener('keydown', updateFocusedTemplateIndex)
      wrapper.removeEventListener('keydown', cycleViaTabs)
      wrapper.removeEventListener('focusin', keepFocusedTemplateIndexInSync)
    }
  }, [block, flatTemplateList, focusedTemplateIndex])

  React.useEffect(() => {
    const handleMouseMove = () => {
      setIsPointerIdle(false)
    }
    const handleKeyDown = () => {
      setIsPointerIdle(true)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  if (ENABLE_SCHEMA_ASSISTANT && aiGeneration !== null) {
    return (
      <AIModelingInput
        mode="fill-document"
        path={path}
        documentTitle={block.title}
        autoFocus
        onExitAiModeling={() => {
          setAiGeneration(null)
          setFocus({
            type: 'block:document-title',
            blockId: idToSourceBlockId(block),
          })
          setIsPointerIdle(true)
          setFocusedTemplateIndex(0)
        }}
      />
    )
  }
  if (isInstanceBlock(block)) {
    return null
  }
  return (
    <div className="space-y-6" ref={templateWrapperRef}>
      {templateList.map((group, groupIndex) => {
        return (
          <div key={groupIndex}>
            {group.name && (
              <p className="text-dark-faint mb-2 text-xs font-medium leading-4">
                {group.name}
                {group.disabled && (
                  <span className="rounded-4xl border-dark-border-solid text-dark-muted group-enabled:group-hover:text-dark-dim ml-2 border px-1 py-0.5 text-[10px] font-medium">
                    SOON
                  </span>
                )}
              </p>
            )}
            {group.templates.map((template, templateIndex) => {
              const templateIndexInAllTemplates = templateList
                .slice(0, groupIndex)
                .reduce(
                  (acc, curr) => acc + curr.templates.length,
                  templateIndex
                )
              const highlighted =
                templateIndexInAllTemplates === focusedTemplateIndex

              return (
                <TemplateItem
                  ref={(ref) => {
                    ref && templateRefs.current.push(ref)
                  }}
                  key={templateIndex}
                  icon={template.icon(highlighted)}
                  label={template.label}
                  description={template.description}
                  disabled={template.disabled}
                  highlighted={highlighted}
                  onSelect={template.onSelect}
                  onMouseEnter={() => {
                    if (template.disabled) return
                    if (isPointerIdle) return
                    setFocusedTemplateIndex(templateIndexInAllTemplates)
                  }}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

const TemplateItem = React.forwardRef<
  HTMLButtonElement,
  {
    icon: React.ReactNode
    label: React.ReactNode
    description: React.ReactNode
    disabled?: boolean
    highlighted?: boolean
    onSelect?: () => void
    onMouseEnter?: () => void
  }
>(
  (
    {
      icon,
      label,
      description,
      disabled = false,
      highlighted = false,
      onSelect,
      onMouseEnter,
    },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        unstyled
        disabled={disabled}
        onClick={onSelect}
        onMouseEnter={onMouseEnter}
        focusStyled
        className={clsx(
          highlighted && 'bg-dark-shade',
          disabled && 'pointer-events-none',
          'text-dark-dim -mx-3 mb-px flex w-[calc(100%+24px)] cursor-default items-center space-x-2 rounded-md px-3 py-2 text-sm leading-4 last:mb-0 disabled:opacity-30'
        )}
      >
        <span className="flex-shrink-0">{icon}</span>
        <span className="inline-flex overflow-hidden whitespace-nowrap text-left">
          <span>{label}</span>
          <span
            className={clsx(
              'text-dark-muted overflow-hidden text-ellipsis whitespace-nowrap text-left',
              !highlighted && 'opacity-0'
            )}
          >
            &nbsp;{description}
          </span>
        </span>
      </Button>
    )
  }
)
